import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@/hooks/useI18n';
import {
    useDebouncedValue,
    usePaymentsFilterOptions,
} from '@/lib/payments/hooks/usePaymentsDashboard';
import {
    facetOptions,
    IFacetOption,
    PaymentsFacetSelection,
    pruneSelection,
} from '@/lib/payments/models/payments-filters.model';

interface UsePaymentsFacetsResult {
    itineraryOptions: IFacetOption[];
    clientOptions: IFacetOption[];
    designerOptions: IFacetOption[];
    /** Testo digitato nella tendina; ha effetto solo quando la lista e' troncata. */
    query: string;
    setQuery: (value: string) => void;
    /** La lista e' parziale: la UI deve dirlo e la ricerca passa al server. */
    truncated: boolean;
    /** Una ricerca lato server e' in volo: la tendina non deve dire "nessun risultato". */
    isSearching: boolean;
    totalCount: number;
    isLoading: boolean;
}

/**
 * Opzioni delle tre tendine dei filtri di /payments.
 *
 * Regge da sola il passaggio fra i due regimi:
 * - **lista completa** (il caso di advisor e agenzia): una chiamata all'apertura, `query`
 *   non viene mai mandata al server e le tendine filtrano in memoria;
 * - **lista troncata** (oltre 500 righe): `query` viene spedita, il database cerca
 *   nell'insieme intero e la tendina mostra cosa torna.
 *
 * Il debounce e' volutamente attivo solo nel secondo regime: nel primo non c'e' nessuna
 * chiamata da ritardare, e far aspettare 350ms un filtro che avviene in memoria e' solo
 * lentezza gratuita.
 */
export const usePaymentsFacets = (
    selection: PaymentsFacetSelection,
    onSelectionPruned: (next: PaymentsFacetSelection) => void
): UsePaymentsFacetsResult => {
    const { t } = useI18n();
    const [query, setQuery] = useState('');
    const debouncedQuery = useDebouncedValue(query);

    // Prima chiamata: nessuna query, si scopre cosi' se la lista sta tutta sotto il tetto.
    const [truncated, setTruncated] = useState(false);

    const includeIds = useMemo(
        () => [selection.itineraryId, selection.designerId].filter((id): id is string => !!id),
        [selection.itineraryId, selection.designerId]
    );

    const { data, isLoading, isFetching } = usePaymentsFilterOptions({
        query: truncated ? debouncedQuery.trim() || null : null,
        includeIds,
        clientKeys: selection.clientKey ? [selection.clientKey] : [],
    });

    // Il regime lo decide il server, non un'euristica sul numero di righe ricevute.
    useEffect(() => {
        if (data?.truncated) setTruncated(true);
    }, [data?.truncated]);

    const rows = useMemo(() => data?.rows ?? [], [data?.rows]);

    // Una selezione rimasta senza righe darebbe una dashboard vuota senza spiegare perche'.
    useEffect(() => {
        const next = pruneSelection(rows, selection, truncated);
        if (next !== selection) onSelectionPruned(next);
        // onSelectionPruned e selection cambiano a ogni render del chiamante: dipendere da
        // loro rilancerebbe l'effetto in continuazione. Le righe sono l'unico input che
        // puo' rendere invalida una selezione gia' valida.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rows, truncated]);

    const unknown = t('payments.filters.unknownOption');

    return {
        itineraryOptions: useMemo(
            () => facetOptions(rows, 'itinerary', selection, unknown),
            [rows, selection, unknown]
        ),
        clientOptions: useMemo(
            () => facetOptions(rows, 'client', selection, unknown),
            [rows, selection, unknown]
        ),
        designerOptions: useMemo(
            () => facetOptions(rows, 'designer', selection, unknown),
            [rows, selection, unknown]
        ),
        query,
        setQuery,
        truncated,
        // Solo nel regime server: in memoria non c'e' nessuna attesa da segnalare. Conta
        // anche il debounce non ancora scaduto, altrimenti fra il tasto premuto e la
        // partenza della fetch la tendina mostrerebbe i risultati vecchi come definitivi.
        isSearching: truncated && (isFetching || query !== debouncedQuery),
        totalCount: data?.total_count ?? 0,
        isLoading,
    };
};
